import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { config } from '../../config';
import { authService } from '../../services/auth';

// Minimal, fixed bottom banner styling consistent with dark theme
const Banner = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(20, 20, 20, 0.98);
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  color: #ffffff;
  padding: 16px;
  z-index: 1000;
`;

const Inner = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const Text = styled.div`
  font-size: 0.95rem;
  color: #dddddd;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 10px 14px;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  color: #ffffff;
  background: ${p => (p.$variant === 'primary' ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'rgba(255,255,255,0.12)')};
  border: ${p => (p.$variant === 'primary' ? 'none' : '1px solid rgba(255,255,255,0.2)')};
`;

const Link = styled.a`
  color: #8fb1ff;
  text-decoration: underline;
`;

const STORAGE_KEY = 'ym_cookie_consent_v1';
const POLICY_VERSION = '1.0';

type ConsentChoice = 'accepted' | 'rejected';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    __ym_ga_loaded__?: boolean;
    __ym_ga_id__?: string;
  }
}

function updateConsent(granted: boolean) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
}

function ensureGAInitialized() {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  if (!window.__ym_ga_loaded__) {
    window.gtag('js', new Date());
    const id = window.__ym_ga_id__;
    if (id) {
      window.gtag('config', id, { anonymize_ip: true });
    }
    window.__ym_ga_loaded__ = true;
  }
}

async function postConsent(choice: 'cookies_accepted' | 'cookies_rejected' | 'tos_accepted' | 'privacy_accepted') {
  const token = authService.getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    await axios.post(`${config.apiUrl}/consent`, { choice, policyVersion: POLICY_VERSION }, { headers });
  } catch (e) {
    // Non-blocking
  }
}

export const CookieBanner: React.FC = () => {
  const [choice, setChoice] = useState<ConsentChoice | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === 'accepted' || raw === 'rejected') {
        setChoice(raw);
        updateConsent(raw === 'accepted');
        if (raw === 'accepted') {
          ensureGAInitialized();
        }
      }
    } catch {
      // ignore
    }
  }, []);

  const accept = useCallback(() => {
    setChoice('accepted');
    try { localStorage.setItem(STORAGE_KEY, 'accepted'); } catch {}
    updateConsent(true);
    ensureGAInitialized();
    postConsent('cookies_accepted');
    postConsent('privacy_accepted');
    postConsent('tos_accepted');
  }, []);

  const essentialOnly = useCallback(() => {
    setChoice('rejected');
    try { localStorage.setItem(STORAGE_KEY, 'rejected'); } catch {}
    updateConsent(false);
    postConsent('cookies_rejected');
    postConsent('privacy_accepted');
    postConsent('tos_accepted');
  }, []);

  if (choice) return null;

  return (
    <Banner>
      <Inner>
        <Text>
          We use cookies for basic functionality and to improve your experience. By accessing or using our Service, you agree to be bound by our <Link href="/privacy">Privacy Policy</Link> and <Link href="/tos">Terms of Service</Link>.
        </Text>
        <Actions>
          <Button $variant="secondary" onClick={essentialOnly}>Accept Terms of Service, Privacy Policy, and essential Cookies</Button>
          <Button $variant="primary" onClick={accept}>Accept Terms of Service, Privacy Policy, and recommended Cookies</Button>
        </Actions>
      </Inner>
    </Banner>
  );
};

export default CookieBanner; 